<template>
    <div
        v-bind:id="formPageId"
        class="form-page"
        v-on:keyup.enter="onKey">
        <div
            v-if="printerFriendly && page.Title && !page.isPaymentPage"
            class="my-3 avoid-page-break-after">
            <div class="h5 pb-2 pt-2 border-top border-bottom form-page-header text-center">
                {{ page.PageNumber + ". " + page.Title }}
            </div>
        </div>
        <project-element
            v-for="element in pageElements"
            ref="projectElements"
            v-bind:key="element.PublicId"
            v-bind:element="element" />
        <div
            v-if="!page.isPaymentPage && hasNoElementsToShow"
            class="fst-italic">
            {{ emptyPageText }}
        </div>
    </div>
</template>
<script>
    import projectElement from "./projectElement.vue";
    import { FORM_TYPES } from "SharedStores/Form/formStore";
    import { PROJECT_FORM_TYPES } from "./projectFormStore";
    import { batchItems, scrollToElement } from "Util/util";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/projectform.i18n";

    const ELEMENT_RENDER_BATCH_SIZE = 10;

    export default {
        components: { "project-element": projectElement },
        props: {
            page: { type: Object, required: true },
            printerFriendly: { type: Boolean, required: false, default: false }
        },
        computed: {
            pageElements() {
                return this.page.FormElements?.map((elementId) =>
                    this.$store.getters[FORM_TYPES.getters.elementFromId](elementId)
                );
            },
            formPageId() {
                return `formPage${this.page.PageNumber}`;
            },
            hasNoElementsToShow() {
                const visibleProjectElements = this.page.FormElements.map((elementId) =>
                    this.$store.getters[PROJECT_FORM_TYPES.getters.projectElementFromId](elementId)
                ).filter((projectElement) => projectElement && projectElement.IsVisible);

                return visibleProjectElements.length <= 0;
            },
            ...mapI18n({ emptyPageText: KEYS.emptyPageText }, NAMESPACE)
        },
        mounted() {
            if (this.$store.getters[PROJECT_FORM_TYPES.getters.doBatchDrawElements]) {
                this._drawElementsBatched();
            }
        },
        methods: {
            onKey(event) {
                const focusable = Array.from(this.$el.querySelectorAll("input, select"));
                const currentIndex = focusable.findIndex((el) => el === event.target);
                if (currentIndex > -1) {
                    let nextIndex = currentIndex + 1;
                    if (nextIndex >= focusable.length) {
                        nextIndex = 0;
                    }
                    focusable[nextIndex].focus();
                    scrollToElement(focusable[nextIndex].id);
                }
            },
            // Render elements programmatically in batches rather than relying on
            // Vue's internal logic for rendering. This is mostly necessary if we
            // have to render a very large number of elements, especially in a browser
            // like IE that doesn't support microtasks
            async _drawElementsBatched() {
                const projectElements = this.$refs.projectElements;
                const batches = batchItems(projectElements, ELEMENT_RENDER_BATCH_SIZE);
                for (const batch of batches) {
                    for (const elt of batch) {
                        elt.draw();
                    }

                    // At least in IE, this seems to result in a slightly smoother
                    // user experience than awaiting $nextTick
                    await this.$app.timeout(0);
                }
            }
        }
    };
</script>
<style lang="scss" scoped>
    .form-page {
        width: 100%;
        position: relative;
        min-height: 400px;
    }

    // No minimum height when printing
    .d-print-block .form-page {
        min-height: unset;
    }

    .form-page-header {
        border-width: 5px !important;
    }
</style>
